import React, { useState } from 'react';
import { Mail, Lock, User, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isEmail } from '../../utils/validation';
import { Button, Input } from '../ui';
import { EXTERNAL_LINKS } from '../../constants/links';
import GoogleSignInButton from './GoogleSignInButton';

interface SignupFormProps {
  onToggleMode: () => void;
  onSuccess: (email: string) => void;
  onBack?: () => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onToggleMode, onSuccess, onBack }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signup } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    if (!isEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await signup(formData.email, formData.password, formData.firstName, formData.lastName);
      if (onSuccess) {
        onSuccess(formData.email);
      }
    } catch (err: unknown) {
      const error = err as { message?: string; error_description?: string };
      const errorMessage = error?.message || error?.error_description || 'Failed to create account';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {onBack && (
        <Button
          onClick={onBack}
          variant="link"
          leftIcon={<ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />}
          className="text-[13px] mb-6"
        >
          Back
        </Button>
      )}

      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
          Get started for free
        </h2>
        <p className="text-[13px] sm:text-sm text-neutral-500">
          No credit card required. Find your first leads in 2 minutes.
        </p>
      </div>

      <div className="mb-6">
        <GoogleSignInButton text="Continue with Google" />
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
        <span className="text-xs text-neutral-400 font-medium">or</span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <Input
            id="firstName"
            name="firstName"
            type="text"
            label="First name"
            value={formData.firstName}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="John"
            autoComplete="given-name"
            autoFocus
            fullWidth
            inputSize="lg"
            leftIcon={<User className="w-4 h-4" />}
          />

          <Input
            id="lastName"
            name="lastName"
            type="text"
            label="Last name"
            value={formData.lastName}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Doe"
            autoComplete="family-name"
            fullWidth
            inputSize="lg"
            leftIcon={<User className="w-4 h-4" />}
          />
        </div>

        <Input
          id="email"
          name="email"
          type="email"
          label="Email"
          value={formData.email}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder="you@company.com"
          autoComplete="email"
          fullWidth
          inputSize="lg"
          leftIcon={<Mail className="w-4 h-4" />}
        />

        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          value={formData.password}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder="Min. 6 characters"
          autoComplete="new-password"
          fullWidth
          inputSize="lg"
          leftIcon={<Lock className="w-4 h-4" />}
          showPasswordToggle
        />

        {error && (
          <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-[13px] text-red-600">{error}</p>
          </div>
        )}

        <Button
          id="signup-submit-btn"
          type="submit"
          loading={isSubmitting}
          fullWidth
          size="lg"
          className="mt-2"
        >
          {isSubmitting ? 'Creating account...' : 'Create account'}
        </Button>
      </form>

      <p className="text-[11px] text-neutral-400 text-center mt-4 leading-relaxed">
        By creating an account, you agree to our{' '}
        <a href={EXTERNAL_LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-neutral-700 underline transition-colors duration-200">Terms</a>
        {' '}and{' '}
        <a href={EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="text-neutral-500 hover:text-neutral-700 underline transition-colors duration-200">Privacy Policy</a>
      </p>

      <div className="text-center mt-5">
        <p className="text-[13px] text-neutral-500">
          Already have an account?{' '}
          <button
            id="sign-in-btn"
            onClick={onToggleMode}
            className="font-semibold text-primary-900 hover:text-primary-700 transition-colors duration-200"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};
